import { create } from 'zustand';
import type { MetricGap } from '@/hooks/useBackfillCheck';

export interface BackfillDay {
  date: string;            // YYYY-MM-DD
  metricKeys: string[];    // Which metrics are missing on this day
  metricLabels: string[];  // Display labels
  metricEmojis: string[];  // Emojis
  done: boolean;           // Manually or auto-marked as done
}

interface BackfillChecklistState {
  /** Whether the checklist is visible at all */
  visible: boolean;
  /** Whether it's expanded or minimized */
  expanded: boolean;
  /** The days that need logging */
  days: BackfillDay[];
  /** The user ID this checklist pertains to */
  userId: string | null;

  /** Initialize checklist from gap data (called when user clicks "Go log them") */
  initFromGaps: (gaps: MetricGap[], userId: string) => void;

  /** Mark a specific day as done (manually by user) */
  markDayDone: (date: string) => void;

  /** Auto-mark a day done if all its metrics have been logged */
  autoMarkDayDone: (date: string) => void;

  /** Toggle expanded / minimized */
  toggleExpanded: () => void;

  /** Close/dismiss the checklist entirely */
  close: () => void;

  /** Set expanded state directly */
  setExpanded: (expanded: boolean) => void;
}

export const useBackfillChecklistStore = create<BackfillChecklistState>((set) => ({
  visible: false,
  expanded: true,
  days: [],
  userId: null,

  initFromGaps: (gaps, userId) => {
    // Build a map: date -> { metricKeys, labels, emojis }
    const dateMap = new Map<string, { keys: string[]; labels: string[]; emojis: string[] }>();
    
    for (const gap of gaps) {
      for (const date of gap.gapDates) {
        const existing = dateMap.get(date) || { keys: [], labels: [], emojis: [] };
        existing.keys.push(gap.metricKey);
        existing.labels.push(gap.metricLabel);
        existing.emojis.push(gap.metricEmoji);
        dateMap.set(date, existing);
      }
    }

    // Sort dates ascending
    const sortedDates = Array.from(dateMap.keys()).sort();

    const days: BackfillDay[] = sortedDates.map(date => {
      const info = dateMap.get(date)!;
      return {
        date,
        metricKeys: info.keys,
        metricLabels: info.labels,
        metricEmojis: info.emojis,
        done: false,
      };
    });

    set({ visible: true, expanded: true, days, userId });
  },

  markDayDone: (date) => {
    set((state) => ({
      days: state.days.map(d => d.date === date ? { ...d, done: true } : d),
    }));
  },

  autoMarkDayDone: (date) => {
    set((state) => ({
      days: state.days.map(d => d.date === date ? { ...d, done: true } : d),
    }));
  },

  toggleExpanded: () => {
    set((state) => ({ expanded: !state.expanded }));
  },

  close: () => {
    set({ visible: false, expanded: true, days: [], userId: null });
  },

  setExpanded: (expanded) => {
    set({ expanded });
  },
}));
